import Icon from '@/components/Icon'
import { Loading } from '@/components/Loading'
import { CopyToClipboardButton } from '@/components/ui/copy-to-clipboard/CopyToClipboardButton'
import { useOrigin } from '@/hooks/useOrigin'
import { getSiteMappings, loadWebsiteConfig } from '@/lib/api'
import clsx from 'clsx'
import { GetStaticProps } from 'next'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Split from 'react-split'

const MOBILE_WIDTH = 560
const GUTTER_SIZE = 8
const MIN_TOTAL_WIDTH = MOBILE_WIDTH * 2 + GUTTER_SIZE
const FIND_SYLLABUS_URL = '/find-syllabus'
const SEARCH_SYLLABUS_URL = '/search-syllabus'

export default function SplitView() {
	const router = useRouter()
	const [leftUrl, setLeftUrl] = useState<string>(FIND_SYLLABUS_URL)
	const [rightUrl, setRightUrl] = useState<string>(FIND_SYLLABUS_URL)
	const [leftSrc, setLeftSrc] = useState<string>(FIND_SYLLABUS_URL)
	const [rightSrc, setRightSrc] = useState<string>(FIND_SYLLABUS_URL)
	const [leftLoading, setLeftLoading] = useState(false)
	const [rightLoading, setRightLoading] = useState(false)
	const origin = useOrigin()
	const isFirstRender = useRef(true)
	const initialWindowWidth = useRef<number | null>(null)

	// Track if we're on client side
	const [isClient, setIsClient] = useState(false)

	// Track fullscreen state
	const [isFullscreen, setIsFullscreen] = useState(false)

	// Add refs for iframes
	const leftIframeRef = useRef<HTMLIFrameElement>(null)
	const rightIframeRef = useRef<HTMLIFrameElement>(null)

	// Add timeout refs to handle stuck loading states
	const leftTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
	const rightTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

	// Handle iframe URL changes
	const handleUrlChange = useCallback(
		(side: 'left' | 'right', newUrl: Location | URL) => {
			try {
				if (!newUrl || typeof window === 'undefined') return

				// Combine pathname, search, and hash without host/protocol
				const urlWithoutHost = `${newUrl.pathname}${newUrl.search}${newUrl.hash}`
				const query = Object.fromEntries(
					new URLSearchParams(window.location.search),
				)

				// Update the URL parameter and stored URL (not iframe src)
				if (side === 'left') {
					query.l = encodeURIComponent(urlWithoutHost)
					setLeftUrl(urlWithoutHost)
				} else {
					query.r = encodeURIComponent(urlWithoutHost)
					setRightUrl(urlWithoutHost)
				}

				if (urlWithoutHost === 'blank') {
					return
				}

				window.history.replaceState(
					{},
					'',
					`${router.pathname}?${new URLSearchParams(
						query as Record<string, string>,
					).toString()}`,
				)
			} catch (error) {
				console.warn('Error updating URL:', error)
			}
		},
		[router],
	)

	// Swap panels
	const handleSwap = useCallback(() => {
		// Clear any existing timeouts before swapping
		if (leftTimeoutRef.current) {
			clearTimeout(leftTimeoutRef.current)
			leftTimeoutRef.current = undefined
		}
		if (rightTimeoutRef.current) {
			clearTimeout(rightTimeoutRef.current)
			rightTimeoutRef.current = undefined
		}

		// Swap URLs and sources
		setLeftUrl(rightUrl)
		setRightUrl(leftUrl)
		setLeftSrc(rightUrl)
		setRightSrc(leftUrl)
		setLeftLoading(true)
		setRightLoading(true)

		// Set new timeouts for the swapped content
		leftTimeoutRef.current = setTimeout(() => {
			setLeftLoading(false)
		}, 1000)

		rightTimeoutRef.current = setTimeout(() => {
			setRightLoading(false)
		}, 1000)
	}, [leftUrl, rightUrl])

	// Toggle fullscreen
	const handleFullscreen = useCallback((iframe: HTMLElement | null) => {
		if (!iframe) return
		if (document.fullscreenElement) {
			document.exitFullscreen()
		} else {
			iframe.requestFullscreen()
		}
	}, [])

	// Handle iframe load - simplified and more reliable
	const handleIframeLoad = useCallback(
		(side: 'left' | 'right', iframe: HTMLIFrameElement) => {
			// Clear the timeout since iframe loaded successfully
			const timeoutRef =
				side === 'left' ? leftTimeoutRef : rightTimeoutRef
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current)
				timeoutRef.current = undefined
			}

			try {
				const newUrl = iframe.contentWindow?.location
				if (newUrl) {
					handleUrlChange(side, newUrl)
				}
			} catch (error) {
				console.warn('Could not access iframe location:', error)
			}

			// Always set loading to false when iframe loads
			if (side === 'left') {
				setLeftLoading(false)
			} else {
				setRightLoading(false)
			}
		},
		[handleUrlChange],
	)

	// Helper function to load new URL in iframe
	const loadUrlInIframe = useCallback(
		(side: 'left' | 'right', url: string) => {
			// Clear any existing timeout
			const timeoutRef =
				side === 'left' ? leftTimeoutRef : rightTimeoutRef
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current)
			}

			if (side === 'left') {
				setLeftLoading(true)
				setLeftUrl(url)
				// FORCE RELOAD: Set blank first, then the actual URL
				setLeftSrc('about:blank')
				setTimeout(() => {
					setLeftSrc(url)
				}, 50)

				// Set fallback timeout for left iframe
				leftTimeoutRef.current = setTimeout(() => {
					setLeftLoading(false)
				}, 1000)
			} else {
				setRightLoading(true)
				setRightUrl(url)
				// FORCE RELOAD: Set blank first, then the actual URL
				setRightSrc('about:blank')
				setTimeout(() => {
					setRightSrc(url)
				}, 50)

				// Set fallback timeout for right iframe
				rightTimeoutRef.current = setTimeout(() => {
					setRightLoading(false)
				}, 1000)
			}
		},
		[],
	)

	// Use useMemo to recalculate whenever initialWindowWidth.current changes
	const initialWidthLargerThanMobile = useMemo(() => {
		if (!isClient) return true // Always show during SSR
		return (
			initialWindowWidth.current !== null &&
			initialWindowWidth.current > MIN_TOTAL_WIDTH
		)
	}, [isClient])

	const copyUrl = useMemo(() => {
		return `${origin}${router.pathname}?l=${encodeURIComponent(
			leftUrl,
		)}&r=${encodeURIComponent(rightUrl)}`
	}, [leftUrl, rightUrl, origin, router.pathname])

	// Handle client-side hydration and window width detection
	useEffect(() => {
		setIsClient(true)
		if (
			initialWindowWidth.current === null &&
			typeof window !== 'undefined'
		) {
			initialWindowWidth.current = window.innerWidth
		}
	}, [])

	// Do not redirect if initial width is larger than MOBILE_WIDTH
	useEffect(() => {
		if (!isClient || initialWindowWidth.current === null) return

		if (initialWindowWidth.current <= MIN_TOTAL_WIDTH) {
			// Only redirect if the initial width was too small for both panels
			router.push('/error?msg=msg_view_side_by_side_on_mobile')
		}
	}, [router, isClient])

	// Listen for fullscreen changes
	useEffect(() => {
		const handleFullscreenChange = () => {
			setIsFullscreen(!!document.fullscreenElement)
		}

		document.addEventListener('fullscreenchange', handleFullscreenChange)
		return () =>
			document.removeEventListener(
				'fullscreenchange',
				handleFullscreenChange,
			)
	}, [])

	// Listen for navigation events from iframes
	useEffect(() => {
		const handleMessage = (event: MessageEvent) => {
			try {
				const data = event.data
				if (
					typeof data === 'object' &&
					data.type === 'routeChangeComplete'
				) {
					const newUrl = new URL(data.url, window.location.origin)
					const side =
						event.source ===
						(
							document.querySelector(
								'iframe[title="Left Panel"]',
							) as HTMLIFrameElement
						)?.contentWindow
							? 'left'
							: 'right'

					// Clear any existing timeout for this side
					const timeoutRef =
						side === 'left' ? leftTimeoutRef : rightTimeoutRef
					if (timeoutRef.current) {
						clearTimeout(timeoutRef.current)
						timeoutRef.current = undefined
					}

					// Set loading state
					if (side === 'left') {
						setLeftLoading(true)
					} else {
						setRightLoading(true)
					}

					// Update the URL
					handleUrlChange(side, newUrl)

					// Set fallback timeout to clear loading after 1 second
					if (side === 'left') {
						leftTimeoutRef.current = setTimeout(() => {
							setLeftLoading(false)
						}, 1500)
					} else {
						rightTimeoutRef.current = setTimeout(() => {
							setRightLoading(false)
						}, 1500)
					}
				} else if (data.type === 'leftUrlChange') {
					// If URL is the same, just show loading briefly for visual feedback
					if (data.url === leftUrl) {
						setLeftLoading(true)
						// Use a minimal timeout for visual feedback only
						setTimeout(() => setLeftLoading(false), 300)
						return
					}
					// Load new URL
					loadUrlInIframe('left', data.url)
				} else if (data.type === 'rightUrlChange') {
					// If URL is the same, just show loading briefly for visual feedback
					if (data.url === rightUrl) {
						setRightLoading(true)
						// Use a minimal timeout for visual feedback only
						setTimeout(() => setRightLoading(false), 300)
						return
					}
					// Load new URL
					loadUrlInIframe('right', data.url)
				}
			} catch (error) {
				console.warn('Error handling message:', error)
			}
		}

		window.addEventListener('message', handleMessage)
		return () => window.removeEventListener('message', handleMessage)
	}, [handleUrlChange, leftUrl, rightUrl, loadUrlInIframe])

	useEffect(() => {
		if (!router.isReady || !isFirstRender.current) return

		const { l, r } = router.query
		const left = l ? decodeURIComponent(l as string) : FIND_SYLLABUS_URL
		const right = r ? decodeURIComponent(r as string) : FIND_SYLLABUS_URL

		setLeftUrl(left)
		setRightUrl(right)
		setLeftSrc(left)
		setRightSrc(right)
		setLeftLoading(true)
		setRightLoading(true)

		// Set initial timeouts for both iframes
		leftTimeoutRef.current = setTimeout(() => {
			setLeftLoading(false)
		}, 1000)

		rightTimeoutRef.current = setTimeout(() => {
			setRightLoading(false)
		}, 1000)

		isFirstRender.current = false

		return () => {
			// Clear timeouts on cleanup
			if (leftTimeoutRef.current) clearTimeout(leftTimeoutRef.current)
			if (rightTimeoutRef.current) clearTimeout(rightTimeoutRef.current)
			setLeftLoading(false)
			setRightLoading(false)
		}
	}, [router.isReady, router.query])

	const renderPanel = (side: 'left' | 'right') => {
		const url = side === 'left' ? leftUrl : rightUrl
		const src = side === 'left' ? leftSrc : rightSrc
		const closeUrl = side === 'left' ? rightUrl : leftUrl
		const title = `${side.charAt(0).toUpperCase() + side.slice(1)} Panel`
		const isLoading = side === 'left' ? leftLoading : rightLoading
		const iframeRef = side === 'left' ? leftIframeRef : rightIframeRef

		return (
			<div className={clsx('absolute inset-0 w-full h-full p-2')}>
				{!url && (
					<div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[110px] text-[#ccc]">
						PAGE
					</div>
				)}
				{url ? (
					<div className="border border-nsw-grey-04 w-full h-full flex flex-col relative z-10">
						<div
							className={clsx(
								'bg-white p-3 flex items-center justify-center gap-6 border-b border-nsw-grey-04',
								isLoading && 'pointer-events-none opacity-50',
							)}
						>
							<button
								type="button"
								className="flex gap-1 text-nsw-brand-dark font-bold"
								onClick={() => {
									if (url === FIND_SYLLABUS_URL) return
									loadUrlInIframe(side, FIND_SYLLABUS_URL)
								}}
								disabled={isLoading}
							>
								<svg
									width="25"
									height="24"
									viewBox="0 0 25 24"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M3.5 18H21.5V16H3.5V18ZM3.5 13H21.5V11H3.5V13ZM3.5 6V8H21.5V6H3.5Z"
										fill="#002664"
									/>
								</svg>

								<span className="underline">Find syllabus</span>
							</button>
							<button
								type="button"
								className="flex gap-1 text-nsw-brand-dark font-bold"
								onClick={() => {
									if (url === SEARCH_SYLLABUS_URL) return
									loadUrlInIframe(side, SEARCH_SYLLABUS_URL)
								}}
								disabled={isLoading}
							>
								<Icon icon="ic:baseline-search" />
								<span className="underline">Search</span>
							</button>
							<a
								href={closeUrl}
								className={clsx(
									'flex gap-1 text-nsw-brand-dark font-bold absolute right-3',
									isLoading && 'pointer-events-none',
								)}
								aria-label="Go to original page"
							>
								<Icon icon="ic:baseline-close" />
							</a>
						</div>
						<div className="relative flex-1">
							{isLoading && (
								<div className="absolute inset-0 z-20 bg-white flex items-center justify-center">
									<Loading />
								</div>
							)}
							<iframe
								ref={iframeRef}
								src={src}
								className={clsx(
									'w-full h-full border-none bg-white',
									isLoading && 'pointer-events-none',
								)}
								data-splitview-side={side}
								title={title}
								sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-downloads allow-modals"
								onLoad={(e) =>
									handleIframeLoad(side, e.currentTarget)
								}
							/>
						</div>
					</div>
				) : null}
			</div>
		)
	}

	return (
		<div className="h-screen w-screen bg-nsw-off-white flex flex-col">
			<div className="flex items-center justify-center gap-3 p-3 bg-nsw-brand-dark text-white">
				<div className="font-bold">View side-by-side</div>
				<div className="absolute left-0 ml-3">
					<a href="/" title="Home" aria-label="Home">
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
						>
							<path
								d="M10 20V14H14V20H19V12H22L12 3L2 12H5V20H10Z"
								fill="white"
							/>
						</svg>
					</a>
				</div>
				<div className="absolute right-0 mr-3 flex items-center gap-2 md:gap-5">
					{/* Swap panels */}
					<button
						onClick={handleSwap}
						title="Swap panels"
						aria-label="Swap panels"
					>
						<svg
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<g clipPath="url(#clip0_26061_281)">
								<path
									d="M6.99 11L3 15L6.99 19V16H14V14H6.99V11ZM21 9L17.01 5V8H10V10H17.01V13L21 9Z"
									fill="white"
								/>
							</g>
							<defs>
								<clipPath id="clip0_26061_281">
									<rect width="24" height="24" fill="white" />
								</clipPath>
							</defs>
						</svg>
					</button>

					{/* Copy/Share link */}
					<CopyToClipboardButton
						textToCopy={copyUrl}
						defaultMessage="Copy link"
						successMessage="Link copied to clipboard"
						buttonStyle="secondary"
						className="w-10 h-10"
						leaveDelay={2000}
						renderButton={(onMouseOut) => (
							<button
								type="button"
								onMouseOut={onMouseOut}
								aria-label="Copy share link"
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="24"
									height="24"
									viewBox="0 0 24 24"
									fill="none"
								>
									<path
										d="M3.9 12C3.9 10.29 5.29 8.9 7 8.9H11V7H7C4.24 7 2 9.24 2 12C2 14.76 4.24 17 7 17H11V15.1H7C5.29 15.1 3.9 13.71 3.9 12ZM8 13H16V11H8V13ZM17 7H13V8.9H17C18.71 8.9 20.1 10.29 20.1 12C20.1 13.71 18.71 15.1 17 15.1H13V17H17C19.76 17 22 14.76 22 12C22 9.24 19.76 7 17 7Z"
										fill="white"
									/>
								</svg>
							</button>
						)}
					></CopyToClipboardButton>

					{/* Fullscreen */}
					<button
						onClick={() =>
							handleFullscreen(document.querySelector('body'))
						}
						title={
							isFullscreen
								? 'Exit fullscreen'
								: 'Enter fullscreen'
						}
					>
						{isFullscreen ? (
							// Minimize icon (exit fullscreen)
							<svg
								xmlns="http://www.w3.org/2000/svg"
								height="24px"
								viewBox="0 -960 960 960"
								width="24px"
								fill="white"
							>
								<path d="m177-120-57-57 184-183H200v-80h240v240h-80v-104L177-120Zm343-400v-240h80v104l183-184 57 57-184 183h104v80H520Z" />
							</svg>
						) : (
							// Maximize icon (enter fullscreen)
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<g clipPath="url(#clip0_maximize)">
									<path
										d="M21 11V3H13L16.29 6.29L6.29 16.29L3 13V21H11L7.71 17.71L17.71 7.71L21 11Z"
										fill="white"
									/>
								</g>
								<defs>
									<clipPath id="clip0_maximize">
										<rect
											width="24"
											height="24"
											fill="white"
										/>
									</clipPath>
								</defs>
							</svg>
						)}
					</button>
				</div>
			</div>
			<Split
				className={clsx(
					`split ${
						initialWidthLargerThanMobile
							? 'flex flex-1 flex-row'
							: 'hidden'
					}`,
				)}
				minSize={[MOBILE_WIDTH, MOBILE_WIDTH]}
				expandToMin
				css={{
					'& .gutter-horizontal': {
						backgroundColor: 'var(--nsw-grey-04)',
						backgroundImage:
							"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='none'%3E%3Cg clip-path='url(%23a)' opacity='.25'%3E%3Cpath fill='%23002664' d='M7.3334 12c0 .7334-.6 1.3334-1.3333 1.3334s-1.3333-.6-1.3333-1.3334c0-.7333.6-1.3333 1.3333-1.3333s1.3333.6 1.3333 1.3333ZM6.0001 6.6667c-.7333 0-1.3333.6-1.3333 1.3333S5.2668 9.3333 6 9.3333 7.3334 8.7333 7.3334 8s-.6-1.3333-1.3333-1.3333Zm0-4c-.7333 0-1.3333.6-1.3333 1.3333S5.2668 5.3334 6 5.3334 7.3334 4.7334 7.3334 4c0-.7333-.6-1.3333-1.3333-1.3333Zm4 2.6667c.7333 0 1.3333-.6 1.3333-1.3334 0-.7333-.6-1.3333-1.3333-1.3333S8.6668 3.2667 8.6668 4s.6 1.3334 1.3333 1.3334Zm0 1.3333c-.7333 0-1.3333.6-1.3333 1.3333s.6 1.3333 1.3333 1.3333 1.3333-.6 1.3333-1.3333-.6-1.3333-1.3333-1.3333Zm0 4c-.7333 0-1.3333.6-1.3333 1.3333 0 .7334.6 1.3334 1.3333 1.3334s1.3333-.6 1.3333-1.3334c0-.7333-.6-1.3333-1.3333-1.3333-1.3333Z'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='a'%3E%3Cpath fill='%23fff' d='M0 0h16v16H0z'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E\");",
					},
				}}
				gutterSize={GUTTER_SIZE}
			>
				<div className="w-full h-full relative">
					{renderPanel('left')}
				</div>
				<div className="w-full h-full relative">
					{renderPanel('right')}
				</div>
			</Split>

			{/* Show mobile message only after client-side hydration */}
			{isClient && (
				<div
					className={`${
						initialWidthLargerThanMobile && 'hidden'
					} text-center mt-12`}
				>
					View side-by-side is not supported on this screen size. Try
					using this tool on a larger screen.
				</div>
			)}

			<style jsx>{`
				:global(html),
				:global(body) {
					min-width: ${MIN_TOTAL_WIDTH}px !important;
					overflow-x: auto !important;
					overflow-y: hidden !important;
				}

				:global(.split) {
					min-width: ${MIN_TOTAL_WIDTH}px !important;
				}

				:global(.gutter) {
					background-color: #eee;
					background-repeat: no-repeat;
					background-position: 50%;
					cursor: col-resize;
				}

				:global(.gutter:hover) {
					background-color: #146cfd;
				}

				.empty-state {
					display: flex;
					align-items: center;
					justify-content: center;
					height: 100%;
					background-color: #f5f5f5;
					color: #666;
					font-size: 1.1rem;
				}
			`}</style>
		</div>
	)
}

export const getStaticProps: GetStaticProps = async ({ preview = false }) => {
	let [config, mappings] = await Promise.all([
		loadWebsiteConfig(preview),
		getSiteMappings(preview),
	])
	return {
		props: {
			mappings,
			preview,
			data: {
				config,
				pageResponse: {
					item: {
						elements: {
							seo__title: {
								value: 'View side-by-side',
							},
						},
						system: {
							codename: 'split_view',
						},
					},
				},
			},
		},
	}
}
